import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { fetchGhostPosts } from '../utils/ghost';
import '../styles/blog.css';

const TAG_COLOR_CLASSES = ['blog-tag-orange', 'blog-tag-green', 'blog-tag-blue'];

function getAuthorInitials(post) {
  const authorName = post.authors?.[0]?.name || 'Author';
  return authorName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function formatPublishedDate(dateValue) {
  if (!dateValue) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(dateValue));
}

function normalizeTag(postTag) {
  return postTag?.name?.trim() || '';
}

export default function BlogScreen() {
  const authModal = useAuthModal();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTag, setActiveTag] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const loadPosts = async () => {
      setIsLoading(true);
      setError('');

      try {
        const ghostPosts = await fetchGhostPosts({ limit: 24 }, controller.signal);
        setPosts(ghostPosts);
      } catch (loadError) {
        setError(loadError.message || 'Failed to load blog posts.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPosts();

    return () => controller.abort();
  }, []);

  const categories = useMemo(() => {
    const tags = new Set();
    posts.forEach((post) => {
      post.tags?.forEach((tag) => {
        const name = normalizeTag(tag);
        if (name) tags.add(name);
      });
    });

    return ['All', ...Array.from(tags)];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return posts.filter((post) => {
      const matchesTag =
        activeTag === 'All' ||
        post.tags?.some((tag) => normalizeTag(tag).toLowerCase() === activeTag.toLowerCase());

      if (!matchesTag) return false;
      if (!normalizedSearch) return true;

      const searchable = [post.title, post.custom_excerpt, post.excerpt]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    });
  }, [activeTag, posts, searchTerm]);

  const featuredPost = filteredPosts[0] || null;
  const latestPosts = filteredPosts.slice(featuredPost ? 1 : 0, (featuredPost ? 1 : 0) + 12);

  const onSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader authModal={authModal} />

      <section className="blog-hero">
        <div className="blog-hero-badge">New articles every week</div>
        <h1 className="blog-hero-title">
          Career Blog.<br />
          <span>Grow with AI.</span>
        </h1>
        <p className="blog-hero-subtitle">
          Expert advice on job search, interview preparation, and career development in IT
        </p>

        <form className="blog-search-container" onSubmit={onSearchSubmit}>
          <div className="blog-search-box">
            <svg
              className="blog-search-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              className="blog-search-input"
              placeholder="Search articles..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <button type="submit" className="blog-search-btn">Search</button>
          </div>
        </form>

        <div className="blog-categories">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={`blog-category-tag ${activeTag === category ? 'active' : ''}`}
              onClick={() => setActiveTag(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      <main className="blog-main-content">
        {isLoading && <p>Loading blog posts...</p>}
        {!isLoading && error && <p>{error}</p>}

        {!isLoading && !error && !featuredPost && (
          <p>No posts found for the selected filters.</p>
        )}

        {!isLoading && !error && featuredPost && (
          <>
            <Link to={`/blog/${featuredPost.slug}`} className="blog-featured-post">
              <div
                className="blog-featured-image"
                style={featuredPost.feature_image ? {
                  backgroundImage: `linear-gradient(135deg, rgba(30, 58, 95, 0.5) 0%, rgba(45, 74, 111, 0.8) 100%), url(${featuredPost.feature_image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                } : undefined}
              />
              <div className="blog-featured-content">
                <span className="blog-featured-label">Featured</span>
                <h2 className="blog-featured-title">{featuredPost.title}</h2>
                <p className="blog-featured-excerpt">{featuredPost.custom_excerpt || featuredPost.excerpt}</p>
                <div className="blog-featured-meta">
                  <div className="blog-author">
                    <div className="blog-author-avatar">{getAuthorInitials(featuredPost)}</div>
                    <div className="blog-author-info">
                      <span className="blog-author-name">{featuredPost.authors?.[0]?.name || 'Ghost Author'}</span>
                      <span className="blog-post-date">{formatPublishedDate(featuredPost.published_at)}</span>
                    </div>
                  </div>
                  <span className="blog-read-time">{featuredPost.reading_time || 1} min read</span>
                </div>
              </div>
            </Link>

            <div className="blog-section-header">
              <h2 className="blog-section-title">Latest Articles</h2>
            </div>

            <div className="blog-grid">
              {latestPosts.map((post, index) => (
                <Link to={`/blog/${post.slug}`} className="blog-card" key={post.id}>
                  <div
                    className={`blog-card-image ${post.feature_image ? '' : `blog-gradient-${(index % 6) + 1}`}`.trim()}
                    style={post.feature_image ? {
                      backgroundImage: `linear-gradient(135deg, rgba(30, 58, 95, 0.35) 0%, rgba(45, 74, 111, 0.45) 100%), url(${post.feature_image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    } : undefined}
                  />
                  <div className="blog-card-content">
                    <div className="blog-card-tags">
                      {(post.tags || []).slice(0, 2).map((tag, tagIndex) => (
                        <span key={`${post.id}-${tag.slug || tag.name}`} className={`blog-tag ${TAG_COLOR_CLASSES[tagIndex % TAG_COLOR_CLASSES.length]}`}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-excerpt">{post.custom_excerpt || post.excerpt}</p>
                    <div className="blog-card-footer">
                      <div className="blog-card-author">
                        <div className="blog-card-avatar" style={{ background: 'linear-gradient(135deg, #667EEA, #764BA2)' }}>
                          {getAuthorInitials(post)}
                        </div>
                        <span className="blog-card-author-name">{post.authors?.[0]?.name || 'Ghost Author'}</span>
                      </div>
                      <span className="blog-card-meta">{post.reading_time || 1} min</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <section className="blog-newsletter">
          <div className="blog-newsletter-content">
            <h2>Subscribe to our Newsletter</h2>
            <p>Get the best articles and career tips delivered straight to your inbox</p>
            <form className="blog-newsletter-form">
              <input
                type="email"
                className="blog-newsletter-input"
                placeholder="Your email"
              />
              <button type="submit" className="blog-newsletter-btn">
                Subscribe
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
